// Enemy archetypes. Each archetype is a small recipe used by the wave
// director and the spawn helpers. Behavior dispatch lives in combat.ts,
// keyed off the EnemyKindComp set at spawn time.

import type { WorldState } from './world-state';
import { C } from './components';
import type {
  Position,
  Velocity,
  Radius,
  Health,
  AttackComp,
  FactionComp,
  RenderInfo,
  EnemyKind,
  EnemyKindComp,
  BomberComp,
} from './components';

export interface EnemyArchetype {
  kind: EnemyKind;
  speed: number;
  maxHealth: number;
  damage: number;
  attackRange: number;
  attackRate: number;
  radius: number;
  color: number;
  /** Used by shooters: projectile launch speed. */
  projectileSpeed: number;
  /** Used by bombers: blast radius / damage. */
  blastRadius?: number;
  blastDamage?: number;
  /** Elites always drop a pickup. */
  guaranteedPickup?: boolean;
}

export const ARCHETYPES: Record<EnemyKind, EnemyArchetype> = {
  seeker: {
    kind: 'seeker',
    speed: 55,
    maxHealth: 4,
    damage: 1,
    attackRange: 14,
    attackRate: 1.0,
    radius: 7,
    color: 0xc44848,
    projectileSpeed: 0,
  },
  shooter: {
    kind: 'shooter',
    speed: 30,
    maxHealth: 5,
    damage: 1.5,
    attackRange: 220,
    attackRate: 0.7,
    radius: 7,
    color: 0xc46d48,
    projectileSpeed: 180,
  },
  bomber: {
    kind: 'bomber',
    speed: 70,
    maxHealth: 6,
    damage: 0, // damage comes from blast
    attackRange: 24, // triggers fuse
    attackRate: 0,
    radius: 9,
    color: 0xa848c4,
    projectileSpeed: 0,
    blastRadius: 64,
    blastDamage: 4,
  },
  swarmer: {
    kind: 'swarmer',
    speed: 85,
    maxHealth: 1.5,
    damage: 0.5,
    attackRange: 12,
    attackRate: 1.4,
    radius: 4,
    color: 0xd0c44c,
    projectileSpeed: 0,
  },
  elite: {
    kind: 'elite',
    speed: 38,
    maxHealth: 30,
    damage: 2.5,
    attackRange: 18,
    attackRate: 0.6,
    radius: 13,
    color: 0xff5050,
    projectileSpeed: 0,
    guaranteedPickup: true,
  },
};

export function spawnEnemyOf(ws: WorldState, kind: EnemyKind, x: number, y: number): number {
  const arch = ARCHETYPES[kind];
  const { world } = ws;
  const id = world.createEntity();
  world.store<Position>(C.position).set(id, { x, y });
  world.store<Velocity>(C.velocity).set(id, { vx: 0, vy: 0 });
  world.store<Radius>(C.radius).set(id, { r: arch.radius });
  world.store<Health>(C.health).set(id, { current: arch.maxHealth, max: arch.maxHealth });
  world.store<AttackComp>(C.attack).set(id, {
    range: arch.attackRange,
    rate: arch.attackRate,
    damage: arch.damage,
    cooldown: 0,
    projectileSpeed: arch.projectileSpeed,
  });
  world.store<FactionComp>(C.faction).set(id, { side: 'enemy' });
  world.store<EnemyKindComp>(C.enemyKind).set(id, { kind });
  world.store<RenderInfo>(C.render).set(id, { color: arch.color, outline: 0x000000 });
  if (arch.kind === 'bomber') {
    world.store<BomberComp>(C.bomber).set(id, {
      fuse: Infinity,
      blastRadius: arch.blastRadius ?? 64,
      blastDamage: arch.blastDamage ?? 4,
    });
  }
  return id;
}
